import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Container, Header, Box, Button, Alert, SpaceBetween, ProgressBar, Select, FormField, Input, Textarea, ExpandableSection
} from '@cloudscape-design/components';
import { imageStudioApi } from './imageStudioApi.js';
import ScenePlanCard from './ScenePlanCard.jsx';

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.svg'];
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff', 'image/svg+xml'];
const MAX_PREVIEWS = 12;

// Operation-specific config
const OPERATION_OPTIONS = {
  resize: {
    ratios: [
      { label: '1:1 (Square)', value: '1:1' },
      { label: '2:3 (Portrait)', value: '2:3' },
      { label: '3:4 (Portrait)', value: '3:4' },
      { label: '4:3 (Landscape)', value: '4:3' },
      { label: '4:5 (Portrait)', value: '4:5' },
    ],
    methods: [
      { label: 'Mirror Blur — soft blurred mirror edges', value: 'mirror-blur' },
      { label: 'Blur Fill — heavy blurred background', value: 'blur-fill' },
      { label: 'Solid Fill — detected background color', value: 'solid-fill' },
      { label: 'Solid Fill: Manual — pick a hex color', value: 'solid-fill-manual' },
      { label: 'AI Extend — generates new background to fill the frame', value: 'outpaint' },
    ],
  },
  // Future operations will add their options here
  'background': {
    bgModes: [
      { label: 'Remove Background (transparent)', value: 'remove' },
      { label: 'Solid Color', value: 'solid' },
      { label: 'AI-Generated Scene', value: 'ai_scene' },
      { label: 'Custom Background Images (up to 5)', value: 'custom' },
      { label: 'AI-Generated Scene (Fire-Red)', value: 'outpaint' },
    ],
    ratios: [
      { label: 'Keep Original', value: 'original' },
      { label: '1:1 (Square)', value: '1:1' },
      { label: '2:3 (Portrait)', value: '2:3' },
      { label: '3:2 (Landscape)', value: '3:2' },
      { label: '3:4 (Portrait)', value: '3:4' },
      { label: '4:3 (Landscape)', value: '4:3' },
      { label: '4:5 (Portrait)', value: '4:5' },
      { label: '16:9 (Wide)', value: '16:9' },
    ],
  },
  'remove-bg': { methods: [{ label: 'Auto detect subject', value: 'auto' }] },
  'change-bg': { methods: [{ label: 'AI generated background', value: 'ai-gen' }] },
  'lifestyle': {
    useCases: [
      { label: 'Product Page (PDP) — product prominent, clean', value: 'pdp' },
      { label: 'Ad Creative — dramatic, with text in image', value: 'ad' },
      { label: 'Social Media — aesthetic, trendy', value: 'social' },
    ],
    ratios: [
      { label: 'Keep Original', value: 'original' },
      { label: '1:1 (Square)', value: '1:1' },
      { label: '2:3 (Portrait)', value: '2:3' },
      { label: '3:2 (Landscape)', value: '3:2' },
      { label: '4:5 (Portrait)', value: '4:5' },
      { label: '9:16 (Stories/Reels)', value: '9:16' },
      { label: '16:9 (Wide)', value: '16:9' },
    ],
  },
  'enhance': { methods: [{ label: 'Auto enhance', value: 'auto' }] },
  'upscale': { methods: [{ label: '2x upscale', value: '2x' }, { label: '4x upscale', value: '4x' }] },
  'convert': {
    formats: [
      { label: 'JPEG (.jpg)', value: 'jpeg' },
      { label: 'PNG (.png)', value: 'png' },
      { label: 'WebP (.webp)', value: 'webp' },
    ],
  },
};

// Model appearance options for "Include a person" feature
const MODEL_APPEARANCE_OPTIONS = [
  { label: 'AI decides (default)', value: 'default' },
  { label: '👩 Indian woman', value: 'indian-woman' },
  { label: '👨 Indian man', value: 'indian-man' },
  { label: '👦 Indian boy (child)', value: 'indian-child-boy' },
  { label: '👧 Indian girl (child)', value: 'indian-child-girl' },
  { label: '👦 Indian teen boy', value: 'indian-teen-boy' },
  { label: '👧 Indian teen girl', value: 'indian-teen-girl' },
  { label: '👩 East Asian woman', value: 'east-asian-woman' },
  { label: '👨 East Asian man', value: 'east-asian-man' },
  { label: '👩 White woman', value: 'white-woman' },
  { label: '👨 White man', value: 'white-man' },
  { label: '👶 White child', value: 'white-child' },
  { label: '👩 Black woman', value: 'black-woman' },
  { label: '👨 Black man', value: 'black-man' },
];

function isImageFile(file) {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return ACCEPTED_EXTENSIONS.includes(ext);
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// Operations that accept a CSV of image URLs as input. Lifestyle is
// excluded — its preview-then-approve flow takes a single product image,
// not a batch from a CSV.
const URL_INPUT_SUPPORTED = new Set(['resize', 'background', 'convert']);

// Soft cap on rows per CSV — matches the backend's 1000-URL guard. Past
// this point we tell the user to split into batches. The backend will
// 400 anything bigger anyway; surfacing it client-side gives a clearer
// error before we round-trip.
const MAX_URLS = 1000;

// Aliases for the URL-only header (back-compat with the pre-product-shaped
// CSV — single column, header optional). We keep this as a fallback so old
// templates still work.
const LEGACY_URL_HEADERS = new Set([
  'url', 'urls',
  'link', 'links',
  'image', 'images',
  'imageurl', 'imageurls',
  'imagelink', 'imagelinks',
  'photo', 'photos',
  'photourl', 'photourls',
  'src',
]);

// Aliases for the "product/SKU" column (column 1 of the new wide CSV).
// Match generously — sellers paste their own master sheets and these
// columns get named all kinds of things.
const PRODUCT_HEADERS = new Set([
  'productname', 'productnameskuid', 'productid', 'productname/skuid',
  'sku', 'skuid', 'sku/productname',
  'product',
  'name',
]);

const normalizeHeader = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Naive CSV row split — handles quoted cells with embedded commas. Good
// enough for the URL+SKU shape we care about; not an RFC-4180-grade parser.
// Bringing in PapaParse for this would be ~45 KB and the failure modes
// (quoted commas inside SKUs/URLs) are uncommon in practice.
function splitCsvRow(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; continue; }
      inQ = !inQ;
      continue;
    }
    if (c === ',' && !inQ) { out.push(cur); cur = ''; continue; }
    cur += c;
  }
  out.push(cur);
  return out.map(s => s.trim());
}

/**
 * Parse a CSV (text) into one of two shapes the backend understands:
 *
 *   { mode: 'product',
 *     rows: [{ product, urls: [string|''] }, ...],
 *     imageColumns: <number of "Image Link N" header columns> }
 *
 *   { mode: 'urls', rows: [{ url, name }, ...] }
 *
 * Detection is header-driven. If column 1's header matches a product/SKU
 * alias AND there's at least one extra column, it's product-shaped. If
 * column 1 is a URL-alias header (or no header at all and the first cell
 * looks like an http URL), it's the legacy URL-only shape.
 *
 * Anything not parseable comes back as { mode: 'urls', rows: [] } with an
 * error message in `warning`.
 */
function parseCsv(text) {
  const lines = (text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return { mode: 'urls', rows: [], warning: '' };

  const headerCells = splitCsvRow(lines[0]);
  const headerNorm = headerCells.map(normalizeHeader);
  const isProductHeader = PRODUCT_HEADERS.has(headerNorm[0]) && headerCells.length > 1;
  const isLegacyUrlHeader = LEGACY_URL_HEADERS.has(headerNorm[0]);

  if (isProductHeader) {
    // Product-shaped CSV — column 1 = product/SKU, columns 2..N = image
    // links (any header text that includes a "link"/"image"/"url" word
    // counts as an image column; cells with no URL stay empty in the
    // output).
    const imageColumns = headerCells.length - 1;
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = splitCsvRow(lines[i]);
      const product = (cells[0] || '').replace(/^"|"$/g, '').trim();
      if (!product) continue;
      const urls = [];
      for (let c = 1; c <= imageColumns; c++) {
        const u = (cells[c] || '').replace(/^"|"$/g, '').trim();
        urls.push(u);
      }
      rows.push({ product, urls });
    }
    return { mode: 'product', rows, imageColumns, warning: '' };
  }

  // Legacy single-column URL CSV (with or without header). Same logic
  // as the previous parseUrlCsv — keep it byte-equivalent so old
  // templates keep working.
  const startIdx = isLegacyUrlHeader ? 1 : 0;
  const rows = [];
  for (let i = startIdx; i < lines.length; i++) {
    const cells = splitCsvRow(lines[i]);
    const url = (cells[0] || '').replace(/^"|"$/g, '').trim();
    const name = (cells[1] || '').replace(/^"|"$/g, '').trim();
    if (!/^https?:\/\//i.test(url)) continue;
    rows.push({ url, name });
  }
  return { mode: 'urls', rows, warning: '' };
}

export default function ProcessingSection({ operation, username, onProcessComplete, onActivityChange }) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState('');
  const fileInputRef = useRef(null);

  // Operation-specific state
  const opConfig = OPERATION_OPTIONS[operation] || {};
  const [ratio, setRatio] = useState(opConfig.ratios?.[0] || null);
  const [method, setMethod] = useState(opConfig.methods?.[0] || null);
  const [targetFormat, setTargetFormat] = useState(opConfig.formats?.[0] || null);
  const [bgMode, setBgMode] = useState(opConfig.bgModes?.[0] || null);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [bgPrompt, setBgPrompt] = useState('');
  const [bgFiles, setBgFiles] = useState([]);
  const bgInputRef = useRef(null);

  // Lifestyle-specific state
  const [useCase, setUseCase] = useState(opConfig.useCases?.[0] || null);
  const [customPrompt, setCustomPrompt] = useState('');

  // Lifestyle — reference image (optional) + preview flow state
  const [referenceFile, setReferenceFile] = useState(null);
  const referenceInputRef = useRef(null);
  const [previewState, setPreviewState] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'approved' | 'failed'
  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [selectedOptionIndices, setSelectedOptionIndices] = useState([0]);

  // Banner text inputs for Ad/Social — text is rendered by Flux directly
  // in the image (not as a post-hoc SVG overlay). Only shown when use-case
  // is Ad (required) or Social (optional).
  const [bannerHeadline, setBannerHeadline] = useState('');
  const [bannerSubtext, setBannerSubtext] = useState('');
  const [bannerCtaLabel, setBannerCtaLabel] = useState('');

  // Include person toggle + model appearance
  const [includePerson, setIncludePerson] = useState({ label: 'No — product only', value: 'no' });
  const [modelAppearance, setModelAppearance] = useState(null);

  // Input source mode: 'files' (drag/drop) or 'urls' (CSV with image links).
  // Hidden and forced to 'files' for lifestyle (see URL_INPUT_SUPPORTED),
  // and for background:ai_scene (which produces 3 variants per image —
  // doesn't fit the wide CSV cell shape, drag-drop only).
  const [inputMode, setInputMode] = useState('files');
  // Parsed URL list when inputMode === 'urls'. Each entry:
  //   { url, name, product?, slot? }
  // For product-shaped CSVs, every non-empty image cell becomes one entry
  // tagged with its product/SKU + 1-based slot index, so the backend's
  // wide-format CSV writer can reassemble the output in the same shape.
  // For legacy single-URL CSVs, product/slot stay undefined and the
  // backend falls back to the long-format CSV.
  const [urlList, setUrlList] = useState([]);
  // CSV upload metadata. csvMode is 'product' or 'urls'; imageColumns is
  // the column count from the source CSV header (lets the backend pin the
  // output to match even when some rows have empty trailing cells).
  const [csvMode, setCsvMode] = useState('urls');
  const [imageColumns, setImageColumns] = useState(0);
  const [productRowCount, setProductRowCount] = useState(0);
  // Original CSV filename, just for display.
  const [csvFileName, setCsvFileName] = useState('');
  const csvInputRef = useRef(null);
  const [csvDragOver, setCsvDragOver] = useState(false);
  // Reset options when operation changes
  useEffect(() => {
    const config = OPERATION_OPTIONS[operation] || {};
    setRatio(config.ratios?.[0] || null);
    setMethod(config.methods?.[0] || null);
    setTargetFormat(config.formats?.[0] || null);
    setBgMode(config.bgModes?.[0] || null);
    setBgColor('#FFFFFF');
    setBgPrompt('');
    setBgFiles([]);
    setUseCase(config.useCases?.[0] || null);
    setCustomPrompt('');
    setReferenceFile(null);
    setPreviewState('idle');
    setPreviewPlan(null);
    setPreviewError(null);
    setSelectedOptionIndices([0]);
    setBannerHeadline('');
    setBannerSubtext('');
    setBannerCtaLabel('');
    setIncludePerson({ label: 'No — product only', value: 'no' });
    setModelAppearance(null);
    // Reset URL-mode state. Force lifestyle back to file mode since URL
    // mode isn't supported there.
    setUrlList([]);
    setCsvFileName('');
    setCsvMode('urls');
    setImageColumns(0);
    setProductRowCount(0);
    if (!URL_INPUT_SUPPORTED.has(operation)) setInputMode('files');
  }, [operation]);

  // ai_scene background isn't compatible with CSV-URL input (it produces
  // 3 variants per image, which doesn't fit the wide-CSV cell shape).
  // If the user picked ai_scene while in URL mode, fall back to files
  // and clear any loaded CSV.
  useEffect(() => {
    if (operation === 'background' && bgMode?.value === 'ai_scene' && inputMode === 'urls') {
      setInputMode('files');
      setUrlList([]);
      setCsvFileName('');
      setCsvMode('urls');
      setImageColumns(0);
      setProductRowCount(0);
    }
  }, [operation, bgMode, inputMode]);

  // Templates dropdown is removed — scene direction now comes from the
  // AI-proposed plan. The old fetchTemplates effect that populated the
  // dropdown has been removed.

  const previews = useMemo(() => {
    return selectedFiles.slice(0, MAX_PREVIEWS).map(file => ({
      file, url: URL.createObjectURL(file)
    }));
  }, [selectedFiles]);

  useEffect(() => {
    return () => previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  // Local object URL for the (optional) lifestyle reference image; revoked on change/unmount.
  const referenceThumbUrl = useMemo(() => {
    return referenceFile ? URL.createObjectURL(referenceFile) : null;
  }, [referenceFile]);

  useEffect(() => {
    return () => { if (referenceThumbUrl) URL.revokeObjectURL(referenceThumbUrl); };
  }, [referenceThumbUrl]);

  // Tell the parent whether the user is actively drafting a new run so it can
  // hide PreviewSection (which shows the last completed run) and avoid the
  // two-cards-at-once confusion. "Active" = files selected, or preview in
  // any non-idle state, or a generation in flight.
  const isActive = selectedFiles.length > 0
    || urlList.length > 0
    || previewState !== 'idle'
    || processing;
  useEffect(() => {
    if (onActivityChange) onActivityChange(isActive);
  }, [isActive, onActivityChange]);

  const handleFiles = (fileList) => {
    const images = Array.from(fileList).filter(isImageFile);
    const skipped = fileList.length - images.length;
    if (images.length === 0) { setError('No valid image files selected.'); return; }
    if (skipped > 0) setError(`${skipped} file${skipped > 1 ? 's' : ''} skipped (unsupported format)`);
    else setError('');
    setSuccess('');
    setSelectedFiles(images);
    // Starting a new batch: clear any stale plan so the fresh upload can be
    // previewed against its own plan rather than carrying over the previous one.
    if (operation === 'lifestyle') {
      setPreviewState('idle');
      setPreviewPlan(null);
      setPreviewError(null);
      setSelectedOptionIndices([0]);
    }
  };

  const removeFile = (index) => setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleZoneClick = () => fileInputRef.current?.click();
  const handleFileInputChange = (e) => { if (e.target.files.length > 0) handleFiles(e.target.files); };

  // ── CSV-with-URLs handlers ──
  const handleCsvFile = async (file) => {
    if (!file) return;
    setError(''); setSuccess('');
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.mode === 'product') {
        // Expand each product row into one entry per non-empty image
        // cell, tagged with product + 1-based slot.
        const entries = [];
        for (const r of parsed.rows) {
          for (let s = 0; s < r.urls.length; s++) {
            const u = r.urls[s];
            if (!u) continue;
            if (!/^https?:\/\//i.test(u)) continue;
            entries.push({
              url: u,
              name: `${r.product}_${s + 1}`,
              product: r.product,
              slot: s + 1,
            });
          }
        }
        if (entries.length === 0) {
          setError(`No image URLs found in CSV. Expected http(s) URLs in the "Image Link 1..N" columns.`);
          return;
        }
        if (entries.length > MAX_URLS) {
          setError(`Found ${entries.length} URLs but the per-run limit is ${MAX_URLS}. Only the first ${MAX_URLS} will be used. Split the CSV into batches.`);
        }
        setCsvMode('product');
        setImageColumns(parsed.imageColumns);
        setProductRowCount(parsed.rows.length);
        setUrlList(entries.slice(0, MAX_URLS));
        setCsvFileName(file.name);
        setSelectedFiles([]);
        return;
      }
      // Legacy single-URL-column CSV.
      const rows = parsed.rows;
      if (rows.length === 0) {
        setError('No image URLs found in CSV. Expecting one URL per row, or a header like "url" / "image_link", or the wide shape "Product Name/SKU ID, Image Link 1, Image Link 2, ...".');
        return;
      }
      if (rows.length > MAX_URLS) {
        setError(`Found ${rows.length} URLs but the per-run limit is ${MAX_URLS}. Only the first ${MAX_URLS} will be used.`);
      }
      setCsvMode('urls');
      setImageColumns(0);
      setProductRowCount(0);
      setUrlList(rows.slice(0, MAX_URLS));
      setCsvFileName(file.name);
      setSelectedFiles([]);
    } catch (err) {
      setError(`Failed to read CSV: ${err.message}`);
    }
  };
  const handleCsvDrop = (e) => {
    e.preventDefault();
    setCsvDragOver(false);
    const f = (e.dataTransfer.files || [])[0];
    if (f) handleCsvFile(f);
  };
  const handleCsvDragOver = (e) => { e.preventDefault(); setCsvDragOver(true); };
  const handleCsvDragLeave = (e) => { e.preventDefault(); setCsvDragOver(false); };
  const handleCsvZoneClick = () => csvInputRef.current?.click();
  const handleCsvInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleCsvFile(f);
    if (e.target) e.target.value = '';
  };
  const removeUrl = (index) => setUrlList(prev => prev.filter((_, i) => i !== index));
  const clearUrls = () => {
    setUrlList([]);
    setCsvFileName('');
    setCsvMode('urls');
    setImageColumns(0);
    setProductRowCount(0);
  };

  // Build the common params for lifestyle API calls (preview + generate).
  // Scene direction comes from the AI-proposed plan, not a template dropdown.
  const buildLifestyleParams = () => ({
    use_case: useCase?.value || 'pdp',
    aspect_ratio: ratio?.value || 'original',
    custom_prompt: customPrompt || '',
    include_person: includePerson?.value || 'no',
    model_appearance: includePerson?.value === 'yes' ? (modelAppearance?.value || 'default') : undefined,
  });

  // Build the ad_copy payload from the banner-text form fields.
  // Returns null when all three fields are empty.
  const buildAdCopy = () => {
    const h = (bannerHeadline || '').trim();
    const s = (bannerSubtext || '').trim();
    const c = (bannerCtaLabel || '').trim();
    if (!h && !s && !c) return null;
    return { headline: h, subtext: s, cta_label: c };
  };

  const handlePreview = async () => {
    if (selectedFiles.length === 0) return;
    setError(''); setSuccess('');
    setPreviewState('loading');
    setPreviewPlan(null);
    setPreviewError(null);

    try {
      const firstFile = selectedFiles[0];
      const imageData = await imageStudioApi.fileToBase64(firstFile);
      const image = { name: firstFile.name, data: imageData };

      let reference_image;
      if (referenceFile) {
        const refData = await imageStudioApi.fileToBase64(referenceFile);
        reference_image = { name: referenceFile.name, data: refData };
      }

      const adCopy = buildAdCopy();

      const plan = await imageStudioApi.previewLifestyle({
        image, reference_image, ...buildLifestyleParams(),
        ad_copy: adCopy || undefined,
      });

      setPreviewPlan(plan);
      // Default: first option checked
      setSelectedOptionIndices(Array.isArray(plan?.options) && plan.options.length > 0 ? [0] : []);
      setPreviewState('ready');
    } catch (err) {
      setPreviewError(err.message || 'Preview request failed.');
      setPreviewState('failed');
    }
  };

  const handleAdjust = () => {
    setPreviewState('idle');
    setPreviewPlan(null);
    setPreviewError(null);
    setSelectedOptionIndices([0]);
  };

  const handleProcess = async () => {
    if (selectedFiles.length === 0 && urlList.length === 0) return;
    // Lifestyle enforces the preview-then-approve flow. The Approve button in
    // ScenePlanCard calls runGeneration() directly and bypasses this path, so
    // if we got here via the normal form button for lifestyle, it's a
    // misclick — ignore it.
    if (operation === 'lifestyle') return;
    await runGeneration();
  };

  const runGeneration = async () => {
    const usingUrls = inputMode === 'urls' && urlList.length > 0;
    if (selectedFiles.length === 0 && !usingUrls) return;
    setProcessing(true);
    setError('');
    setSuccess('');
    setPhase('processing');
    const total = usingUrls ? urlList.length : selectedFiles.length;
    setProgress({ completed: 0, total });

    try {
      const runId = imageStudioApi.createRunId(username);
      const params = {
        operation,
        user: username,
        ratio: ratio?.value || '',
        method: method?.value || '',
        target_format: targetFormat?.value || '',
        bg_mode: bgMode?.value || '',
        bg_color: bgColor || '#FFFFFF',
        bg_prompt: bgPrompt || '',
        aspect_ratio: (operation === 'background' || operation === 'lifestyle') && ratio ? ratio.value : '',
        // Lifestyle params
        use_case: operation === 'lifestyle' ? (useCase?.value || 'pdp') : '',
        custom_prompt: operation === 'lifestyle' ? (customPrompt || '') : '',
        include_person: operation === 'lifestyle' ? (includePerson?.value || 'no') : undefined,
        model_appearance: operation === 'lifestyle' && includePerson?.value === 'yes' ? (modelAppearance?.value || 'default') : undefined,
        // Wide-CSV column count — only meaningful when input came from a
        // product-shaped CSV. The backend uses this to pin the output
        // column count to match the user's source even when some rows had
        // empty trailing cells. Send 0 for non-product runs (drag-drop,
        // legacy single-URL CSV) — backend treats it as "infer from data".
        image_columns: usingUrls && csvMode === 'product' ? imageColumns : 0,
      };

      // Attach the approved plan + selected option indices + ad_copy
      if (operation === 'lifestyle') {
        const adCopy = buildAdCopy();

        if (previewPlan && !previewPlan.preview_unavailable) {
          params.preview_plan = previewPlan;
          params.selected_option_indices = selectedOptionIndices;

          // If only one option is selected, also send approved_option for
          // backward compat with the backend's single-option path.
          if (selectedOptionIndices.length === 1) {
            const opts = Array.isArray(previewPlan.options) ? previewPlan.options : [];
            if (opts[selectedOptionIndices[0]]) {
              params.approved_option = opts[selectedOptionIndices[0]];
            }
          }
        }

        // Send ad_copy for Ad/Social text-in-image
        if (adCopy) params.ad_copy = adCopy;

        if (referenceFile) {
          const refData = await imageStudioApi.fileToBase64(referenceFile);
          params.reference_image = { name: referenceFile.name, data: refData };
        }
      }

      // For custom background mode, convert bg files to base64
      if (operation === 'background' && bgMode?.value === 'custom' && bgFiles.length > 0) {
        const bgImages = [];
        for (const f of bgFiles.slice(0, 5)) {
          const data = await imageStudioApi.fileToBase64(f);
          bgImages.push({ name: f.name, data });
        }
        params.bg_images = bgImages;
      }

      const results = await imageStudioApi.processImages(
        usingUrls ? urlList : selectedFiles, runId, params,
        (p) => setProgress({ completed: p.completed, total: p.total, phase: p.phase || '' }),
        { mode: usingUrls ? 'urls' : 'files' }
      );

      // For async ops (background, lifestyle), results are saved by the Lambda — just refresh.
      // URL-mode resize/convert also go async now (worker-side fetch), so
      // they follow the same "worker already wrote everything" path.
      const workerWroteResults = params.operation === 'background'
        || params.operation === 'lifestyle'
        || (usingUrls && (params.operation === 'resize' || params.operation === 'convert' || !params.operation));
      if (workerWroteResults) {
        setPhase('done');
        const opLabel = params.operation === 'lifestyle' ? 'Lifestyle'
          : params.operation === 'background' ? 'Background'
          : operationLabel;
        setSuccess(`${opLabel} processing completed 🎉`);
        setSelectedFiles([]);
        setBgFiles([]);
        setUrlList([]);
        setCsvFileName('');
        setCsvMode('urls');
        setImageColumns(0);
        setProductRowCount(0);
        // Reset lifestyle preview state on completion. The approved plan is
        // persisted with the run and rendered by PreviewSection alongside
        // the output images — keeping it here too would duplicate it on
        // screen. This component is the "work on the current batch" area;
        // it should go back to idle so the user can start a new one.
        setReferenceFile(null);
        setPreviewState('idle');
        setPreviewPlan(null);
        setPreviewError(null);
        setSelectedOptionIndices([0]);
        if (onProcessComplete) onProcessComplete({
          run_id: runId,
          operation: params.operation,
          user: username,
          total_images: total,
        });
      } else {
        // Sync ops — save results via API
        setPhase('saving');
        const { metadata } = await imageStudioApi.saveResults(runId, username, results, params);

        setPhase('done');
        const okCount = metadata.success_count || 0;
        const failCount = metadata.failed_count || 0;
        let msg = `${okCount} image${okCount !== 1 ? 's' : ''} processed successfully 🎉`;
        if (failCount > 0) msg += ` (${failCount} failed)`;
        setSuccess(msg);
        setSelectedFiles([]);
        setUrlList([]);
        setCsvFileName('');
        setCsvMode('urls');
        setImageColumns(0);
        setProductRowCount(0);
        if (onProcessComplete) onProcessComplete(metadata);
      }
    } catch (err) {
      setError(err.message || 'Processing failed.');
      // On failure, reset the lifestyle preview state so the form comes back
      // editable and the user can adjust and try again.
      if (operation === 'lifestyle') {
        setPreviewState('idle');
      }
    } finally {
      setProcessing(false);
      setPhase('');
    }
  };

  const dropZoneClasses = ['image-link-drop-zone', dragOver ? 'drag-over' : '', processing ? 'disabled' : ''].filter(Boolean).join(' ');
  const progressLabel = phase === 'saving' ? 'Saving results...'
    : phase === 'done' ? 'Done ✅'
    : phase === 'submitting' ? 'Submitting job...'
    : phase === 'processing' ? 'Processing in background... (this may take a minute)'
    : `Processing ${progress.completed} of ${progress.total}...`;

  const operationLabel = {
    resize: 'Resize', 'remove-bg': 'Remove Background', 'change-bg': 'Change Background',
    enhance: 'Enhance', upscale: 'Upscale', convert: 'Convert', lifestyle: 'Generate Lifestyle',
    background: 'Background',
  }[operation] || 'Process';

  return (
    <Container header={<Header variant="h2" description="Select images and configure processing options">📸 {operationLabel} Images</Header>}>
      <SpaceBetween size="m">
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

        {/* Input source toggle — only shown for operations that support a
            CSV-of-URLs input. Lifestyle skips this entirely.
            Background → AI Scene also skips it: ai_scene produces 3 variants
            per image, which doesn't fit the wide-CSV cell shape, so the
            backend rejects it and the UI should match. */}
        {URL_INPUT_SUPPORTED.has(operation)
          && !(operation === 'background' && bgMode?.value === 'ai_scene') && (
          <div className="studio-input-mode">
            <button
              type="button"
              className={`studio-input-mode-tab ${inputMode === 'files' ? 'active' : ''}`}
              onClick={() => setInputMode('files')}
              disabled={processing}
            >
              📁 Local files
            </button>
            <button
              type="button"
              className={`studio-input-mode-tab ${inputMode === 'urls' ? 'active' : ''}`}
              onClick={() => setInputMode('urls')}
              disabled={processing}
            >
              🔗 CSV with image URLs
            </button>
          </div>
        )}

        {inputMode === 'files' && (
          <div>
            <div role="button" tabIndex={0} className={dropZoneClasses}
              onClick={handleZoneClick} onDrop={handleDrop}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleZoneClick(); }}
              aria-label="Drop images here or click to browse">
              <p className="drop-zone-icon">📁</p>
              <p className="drop-zone-title">Drag & drop images here, or click to select</p>
              <p className="drop-zone-hint">JPG · PNG · GIF · WebP · BMP · TIFF · SVG</p>
            </div>
            <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS.join(',')}
              multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
          </div>
        )}

        {inputMode === 'urls' && (
          <div>
            <div
              role="button" tabIndex={0}
              className={['image-link-drop-zone', csvDragOver ? 'drag-over' : '', processing ? 'disabled' : ''].filter(Boolean).join(' ')}
              onClick={handleCsvZoneClick} onDrop={handleCsvDrop}
              onDragOver={handleCsvDragOver} onDragLeave={handleCsvDragLeave}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCsvZoneClick(); }}
              aria-label="Drop a CSV file here or click to select">
              <p className="drop-zone-icon">📄</p>
              <p className="drop-zone-title">Drag & drop a CSV with image URLs, or click to select</p>
              <p className="drop-zone-hint">
                Two supported shapes:
                <br />
                <strong>Product-shaped (recommended):</strong> <code>Product Name/SKU ID</code>, <code>Image Link 1</code>, <code>Image Link 2</code>, …
                — output mirrors the input layout, URLs swapped in place.
                <br />
                <strong>Single-URL (legacy):</strong> one URL per row, optional header like <code>url</code> / <code>image_url</code> / <code>photo_url</code>.
                Up to {MAX_URLS} URLs per run.
              </p>
            </div>
            <input ref={csvInputRef} type="file" accept=".csv,text/csv"
              style={{ display: 'none' }} onChange={handleCsvInputChange} />
            <Box margin={{ top: 'xxs' }} fontSize="body-s" color="text-body-secondary">
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent('Product Name/SKU ID,Image Link 1,Image Link 2,Image Link 3,Image Link 4,Image Link 5\nSKU-001,https://example.com/image-1a.jpg,https://example.com/image-1b.jpg,,,\nSKU-002,https://example.com/image-2a.jpg,,,,\n')}`}
                download="image-urls-template.csv"
              >Download CSV template</a>
              {' '}— images are downloaded server-side, so any public http(s) URL works. The output CSV mirrors your input column layout.
            </Box>
          </div>
        )}

        {/* URL list preview */}
        {inputMode === 'urls' && urlList.length > 0 && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
              {csvMode === 'product' ? (
                <>
                  {productRowCount} product{productRowCount !== 1 ? 's' : ''} ·{' '}
                  {urlList.length} image{urlList.length !== 1 ? 's' : ''} ·{' '}
                  {imageColumns} image column{imageColumns !== 1 ? 's' : ''}
                </>
              ) : (
                <>
                  {urlList.length} URL{urlList.length !== 1 ? 's' : ''} loaded
                </>
              )}
              {csvFileName ? ` from ${csvFileName}` : ''}
              {' · '}
              <button type="button" className="studio-link-btn" onClick={clearUrls} disabled={processing}>
                Clear
              </button>
            </Box>
            <div className="file-preview-grid">
              {urlList.slice(0, MAX_PREVIEWS).map((u, i) => (
                <div key={i} className="file-preview-item">
                  <img
                    src={u.url}
                    alt={u.name || u.url}
                    className="file-preview-thumb"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                  <div className="file-preview-info">
                    <span className="file-preview-name" title={u.url}>
                      {u.product
                        ? `${u.product.length > 14 ? u.product.slice(0, 12) + '…' : u.product} #${u.slot}`
                        : ((u.name || u.url).length > 22
                          ? (u.name || u.url).slice(0, 20) + '…'
                          : (u.name || u.url))
                      }
                    </span>
                  </div>
                  <button className="file-preview-remove" onClick={() => removeUrl(i)} aria-label={`Remove ${u.url}`}>✕</button>
                </div>
              ))}
              {urlList.length > MAX_PREVIEWS && (
                <div className="file-preview-more">+{urlList.length - MAX_PREVIEWS} more</div>
              )}
            </div>
            <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
              Thumbnails are loaded directly from the URL. If a thumbnail fails to display, the server may still be able to fetch it.
            </Box>
          </div>
        )}

        {/* File previews */}
        {inputMode === 'files' && selectedFiles.length > 0 && (
          <div>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
              {selectedFiles.length} image{selectedFiles.length !== 1 ? 's' : ''} selected
            </Box>
            <div className="file-preview-grid">
              {previews.map((p, i) => (
                <div key={i} className="file-preview-item">
                  <img src={p.url} alt={p.file.name} className="file-preview-thumb" />
                  <div className="file-preview-info">
                    <span className="file-preview-name" title={p.file.name}>
                      {p.file.name.length > 20 ? p.file.name.slice(0, 18) + '...' : p.file.name}
                    </span>
                    <span className="file-preview-size">{formatSize(p.file.size)}</span>
                  </div>
                  <button className="file-preview-remove" onClick={() => removeFile(i)} aria-label={`Remove ${p.file.name}`}>✕</button>
                </div>
              ))}
              {selectedFiles.length > MAX_PREVIEWS && (
                <div className="file-preview-more">+{selectedFiles.length - MAX_PREVIEWS} more</div>
              )}
            </div>
          </div>
        )}

        {/* Operation-specific options */}
        <SpaceBetween direction="horizontal" size="m">
          {/* Aspect ratio: shown here only for operations that don't have
              a more specific "Output Aspect Ratio" field inside their own
              section below (background, lifestyle). For those, the per-
              operation field owns the label and positioning; rendering
              this generic one too would produce duplicate controls wired
              to the same state. */}
          {opConfig.ratios && operation !== 'background' && operation !== 'lifestyle' && (
            <FormField label="Aspect Ratio">
              <Select selectedOption={ratio} onChange={({ detail }) => setRatio(detail.selectedOption)}
                options={opConfig.ratios} disabled={processing} />
            </FormField>
          )}
          {opConfig.methods && (
            <FormField label={operation === 'resize' ? 'Resize Method' : 'Method'}>
              <Select selectedOption={method} onChange={({ detail }) => setMethod(detail.selectedOption)}
                options={opConfig.methods} disabled={processing} />
            </FormField>
          )}
          {opConfig.formats && (
            <FormField label="Target Format">
              <Select selectedOption={targetFormat} onChange={({ detail }) => setTargetFormat(detail.selectedOption)}
                options={opConfig.formats} disabled={processing} />
            </FormField>
          )}

          {opConfig.bgModes && (
            <FormField label="Background Mode">
              <Select selectedOption={bgMode} onChange={({ detail }) => setBgMode(detail.selectedOption)}
                options={opConfig.bgModes} disabled={processing} />
            </FormField>
          )}
        </SpaceBetween>

        {/* Background-specific options */}
        {operation === 'resize' && method?.value === 'solid-fill-manual' && (
          <FormField label="Fill Color" description="Pick a color to pad the image with — same control as Background → Solid">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                disabled={processing}
                style={{ width: 40, height: 40, border: '1px solid #e9ebed', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                aria-label="Pick fill color"
              />
              <Input value={bgColor} onChange={({ detail }) => setBgColor(detail.value)}
                placeholder="#FFFFFF" disabled={processing} />
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { hex: '#FFFFFF', name: 'White' },
                  { hex: '#000000', name: 'Black' },
                  { hex: '#FF0000', name: 'Red' },
                  { hex: '#0000FF', name: 'Blue' },
                  { hex: '#00AA00', name: 'Green' },
                  { hex: '#FFDD00', name: 'Yellow' },
                  { hex: '#FF69B4', name: 'Pink' },
                  { hex: '#808080', name: 'Gray' },
                ].map(({ hex, name }) => (
                  <button
                    key={hex}
                    type="button"
                    title={name}
                    onClick={() => setBgColor(hex)}
                    disabled={processing}
                    aria-label={`Select ${name}`}
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      backgroundColor: hex,
                      border: bgColor.toUpperCase() === hex ? '3px solid #0972d3' : '2px solid #d1d5db',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          </FormField>
        )}

        {operation === 'background' && bgMode?.value === 'solid' && (
          <FormField label="Background Color" description="Pick a color or enter a hex code">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                disabled={processing}
                style={{ width: 40, height: 40, border: '1px solid #e9ebed', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                aria-label="Pick background color"
              />
              <Input value={bgColor} onChange={({ detail }) => setBgColor(detail.value)}
                placeholder="#FFFFFF" disabled={processing} />
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { hex: '#FFFFFF', name: 'White' },
                  { hex: '#000000', name: 'Black' },
                  { hex: '#FF0000', name: 'Red' },
                  { hex: '#0000FF', name: 'Blue' },
                  { hex: '#00AA00', name: 'Green' },
                  { hex: '#FFDD00', name: 'Yellow' },
                  { hex: '#FF69B4', name: 'Pink' },
                  { hex: '#808080', name: 'Gray' },
                ].map(({ hex, name }) => (
                  <button
                    key={hex}
                    type="button"
                    title={name}
                    onClick={() => setBgColor(hex)}
                    disabled={processing}
                    aria-label={`Select ${name}`}
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      backgroundColor: hex,
                      border: bgColor.toUpperCase() === hex ? '3px solid #0972d3' : '2px solid #d1d5db',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          </FormField>
        )}

        {operation === 'background' && bgMode?.value === 'ai_scene' && (
          <FormField label="Scene Description" description="Describe the background you want (3 variations will be generated)">
            <Textarea value={bgPrompt} onChange={({ detail }) => setBgPrompt(detail.value)}
              placeholder="e.g., modern office desk with soft lighting, wooden table in a café, outdoor garden"
              rows={2} disabled={processing} />
          </FormField>
        )}

        {operation === 'background' && bgMode?.value === 'custom' && (
          <FormField label="Background Images (up to 5)" description="Upload background images — one output per background">
            <input ref={bgInputRef} type="file" accept="image/*" multiple
              onChange={(e) => setBgFiles(Array.from(e.target.files || []).slice(0, 5))}
              disabled={processing} />
            {bgFiles.length > 0 && (
              <Box margin={{ top: 'xxs' }} fontSize="body-s" color="text-body-secondary">
                {bgFiles.length} background{bgFiles.length !== 1 ? 's' : ''} selected
              </Box>
            )}
          </FormField>
        )}

        {operation === 'background' && bgMode?.value === 'outpaint' && (
          <FormField label="Scene Description" description="Describe the background you want — Fire-Red replaces the background and fills the selected aspect ratio in one pass.">
            <Textarea value={bgPrompt} onChange={({ detail }) => setBgPrompt(detail.value)}
              placeholder="e.g., modern office desk with soft lighting, wooden table in a café, outdoor garden"
              rows={2} disabled={processing} />
          </FormField>
        )}

        {operation === 'background' && opConfig.ratios && (
          <FormField label="Output Aspect Ratio">
            <Select selectedOption={ratio} onChange={({ detail }) => setRatio(detail.selectedOption)}
              options={opConfig.ratios} disabled={processing} />
          </FormField>
        )}

        {/* Lifestyle-specific options */}
        {operation === 'lifestyle' && (
          <SpaceBetween size="m">
            <SpaceBetween direction="horizontal" size="m">
              <FormField label="Use Case">
                <Select selectedOption={useCase}
                  onChange={({ detail }) => setUseCase(detail.selectedOption)}
                  options={opConfig.useCases || []} disabled={processing} />
              </FormField>
              <FormField label="Output Aspect Ratio">
                <Select selectedOption={ratio}
                  onChange={({ detail }) => setRatio(detail.selectedOption)}
                  options={opConfig.ratios || []} disabled={processing} />
              </FormField>
            </SpaceBetween>
            <FormField label="Scene Description (optional)" description="Describe what you want. The AI will propose 3 distinct creative directions based on this.">
              <Textarea value={customPrompt} onChange={({ detail }) => setCustomPrompt(detail.value)}
                placeholder="e.g., kid wearing this in a playground, summer vibes"
                rows={2} disabled={processing} />
            </FormField>
            {/* Person in scene toggle + model appearance */}
            <SpaceBetween direction="horizontal" size="m">
              <FormField label="Include a person in the scene?">
                <Select selectedOption={includePerson}
                  onChange={({ detail }) => {
                    setIncludePerson(detail.selectedOption);
                    if (detail.selectedOption.value === 'yes' && !modelAppearance) {
                      setModelAppearance(MODEL_APPEARANCE_OPTIONS[0]);
                    }
                  }}
                  options={[
                    { label: 'No — product only', value: 'no' },
                    { label: 'Yes — show with a person', value: 'yes' },
                  ]} disabled={processing} />
              </FormField>
              {includePerson?.value === 'yes' && (
                <FormField label="Model appearance">
                  <Select selectedOption={modelAppearance}
                    onChange={({ detail }) => setModelAppearance(detail.selectedOption)}
                    options={MODEL_APPEARANCE_OPTIONS} disabled={processing} />
                </FormField>
              )}
            </SpaceBetween>
            {/* Banner text — shown for Ad (required) and Social (optional).
                Hidden for PDP since PDP images never have text.
                Text is rendered by Flux directly in the image. */}
            {(useCase?.value === 'ad' || useCase?.value === 'social') && (
              <ExpandableSection
                headerText={useCase?.value === 'ad' ? 'Text in image (recommended for ads)' : 'Text in image (optional)'}
                headerDescription="This text will be rendered directly in the generated image by the AI model — naturally integrated into the scene."
                defaultExpanded={useCase?.value === 'ad'}
              >
                <SpaceBetween size="xs">
                  <FormField label="Headline" description={useCase?.value === 'ad' ? 'Required for ad creatives. 2–8 words works best.' : 'Optional. Keep it short and punchy.'}>
                    <Input
                      value={bannerHeadline}
                      onChange={({ detail }) => setBannerHeadline(detail.value)}
                      placeholder='e.g., SUMMER SALE 40% OFF'
                      disabled={processing || previewState === 'loading'}
                    />
                  </FormField>
                  <FormField label="Subtext (optional)">
                    <Input
                      value={bannerSubtext}
                      onChange={({ detail }) => setBannerSubtext(detail.value)}
                      placeholder="e.g., Limited time only"
                      disabled={processing || previewState === 'loading'}
                    />
                  </FormField>
                  <FormField label="CTA button text (optional)" description="Rendered as a button/badge in the image">
                    <Input
                      value={bannerCtaLabel}
                      onChange={({ detail }) => setBannerCtaLabel(detail.value)}
                      placeholder="e.g., Shop Now"
                      disabled={processing || previewState === 'loading'}
                    />
                  </FormField>
                  <Box fontSize="body-s" color="text-body-secondary">
                    The AI model will render this text naturally in the image — with proper shadows, perspective, and integration into the scene.
                  </Box>
                </SpaceBetween>
              </ExpandableSection>
            )}
            <FormField
              label="Reference image (optional)"
              description="Upload a lifestyle photo whose style and mood you want to emulate. We'll factor it into the scene plan before generating."
            >
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <input
                  ref={referenceInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS.join(',')}
                  onChange={(e) => {
                    const f = (e.target.files || [])[0];
                    if (f && isImageFile(f)) setReferenceFile(f);
                    // Clear the input value so selecting the same file twice still fires onChange.
                    if (e.target) e.target.value = '';
                  }}
                  disabled={processing || previewState === 'loading'}
                />
                {referenceFile && (
                  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    {referenceThumbUrl && (
                      <img
                        src={referenceThumbUrl}
                        alt="Reference"
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #e9ebed' }}
                      />
                    )}
                    <Box fontSize="body-s" color="text-body-secondary">
                      {referenceFile.name.length > 28 ? referenceFile.name.slice(0, 26) + '…' : referenceFile.name}
                    </Box>
                    <Button variant="link" onClick={() => setReferenceFile(null)} disabled={processing}>Remove</Button>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        )}

        {/* Lifestyle scene-plan card — shown from the moment the user clicks
            Preview, through approval, processing, and the success state.
            Interactive (Approve/Adjust) only while previewState is 'ready' or
            'failed' and generation hasn't started yet. */}
        {operation === 'lifestyle' && previewState !== 'idle' && (
          <ScenePlanCard
            plan={previewPlan}
            loading={previewState === 'loading'}
            error={previewState === 'failed' && !processing ? previewError : null}
            referenceThumbUrl={referenceThumbUrl}
            originalThumbUrl={previews[0]?.url || null}
            inputs={{
              useCase: useCase?.value || '',
              aspectRatio: ratio?.value || '',
              customPrompt: customPrompt || '',
              adCopy: buildAdCopy(),
              tweak: '',
              includePerson: includePerson?.value || 'no',
              modelAppearance: includePerson?.value === 'yes' ? (modelAppearance?.value || 'default') : '',
            }}
            readOnly={processing || previewState === 'approved'}
            selectedIndices={selectedOptionIndices}
            onSelectionChange={(indices) => setSelectedOptionIndices(indices)}
            useCase={useCase?.value || 'pdp'}
            onApprove={() => {
              setPreviewState('approved');
              runGeneration();
            }}
            onAdjust={handleAdjust}
          />
        )}

        {processing && (
          <ProgressBar
            value={progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}
            additionalInfo={progressLabel} label="Progress"
            status={phase === 'done' ? 'success' : 'in-progress'}
          />
        )}

        {selectedFiles.length > 0 && operation === 'lifestyle' && previewState === 'idle' && (
          <Button variant="primary" onClick={handlePreview} loading={false} disabled={processing}>
            🔍 Preview plan
          </Button>
        )}

        {(selectedFiles.length > 0 || urlList.length > 0) && operation !== 'lifestyle' && (
          <Button variant="primary" onClick={handleProcess} loading={processing} disabled={processing}>
            🚀 {operationLabel} {inputMode === 'urls' ? urlList.length : selectedFiles.length} Image{(inputMode === 'urls' ? urlList.length : selectedFiles.length) !== 1 ? 's' : ''}
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}
